import { z } from 'zod';
import { createPaginatedResponseSchema, LowercaseUuidSchema } from './common.js';
// One-directional: supplier-invoice.ts does not import this file (only names it
// in a comment), so this reference cannot cycle - which matters because Zod
// schemas are runtime values and a cycle would leave one `undefined` at load.
import { SupplierInvoiceLineItemSchema } from './supplier-invoice.js';
import { RecipientParticularsStatusSchema, RecipientParticularsValidationSchema, } from '../tax-invoice-particulars.js';
import { extractInclusiveVat, effectiveVatRateBasisPoints, SA_VAT_RATE_BASIS_POINTS } from '../vat.js';
// =============================================================================
// Supplier Invoice Extraction (async vision-LLM pre-fill of the create form)
// =============================================================================
/**
 * MIME types accepted for an extraction upload - a PDF or a photo of an invoice
 * or till slip. Exported for frontend validation reuse, and consumed by the
 * backend's `SUPPLIER_INVOICE_EXTRACTION` document-registry entry so the client
 * guard and the server allowlist cannot drift: a client that offered a type the
 * server rejects turns a readable document into an upload error, and one that
 * withheld a type the server accepts (which is what happened - the web form was
 * PDF-only while chat and WhatsApp already took images) silently sends users to
 * manual entry.
 *
 * Every entry must be natively decodable by the Gemini vision model, because the
 * extractor sends the stored buffer as-is with its server-derived mime type.
 *
 * A deliberate SUBSET of `ALLOWED_ACTIVITY_ATTACHMENT_MIME_TYPES`, and the two
 * lists must not be merged:
 *  - `image/heic` is excluded. It is a genuine deferral, not an oversight -
 *    Gemini HEIC decode cannot be smoke-tested without a real
 *    GOOGLE_CLOUD_PROJECT, and WhatsApp transcodes photo-sends to JPEG, so HEIC
 *    arrives only via a rare document-send. iPhone camera-roll picks CAN be
 *    HEIC, so the frontend names that case specifically rather than reporting a
 *    generic wrong-type error.
 *  - DOCX and XLSX are excluded: they are not vision-extractable at all.
 */
export const ALLOWED_SUPPLIER_INVOICE_EXTRACTION_MIME_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
];
/**
 * Maximum upload size for an extraction. The single source of truth shared by the
 * extraction route's `maxFileBytes` and the CLI's pre-download size pre-check, so
 * those two can never silently drift. The SUPPLIER_INVOICE_EXTRACTION
 * document-registry cap is a separate declaration this value is chosen to match,
 * not bound to. Also stays under the Vertex inline-payload ceiling.
 */
export const SUPPLIER_INVOICE_EXTRACTION_MAX_FILE_BYTES = 10 * 1024 * 1024;
/**
 * Querystring of the extraction upload (the multipart body is the file itself, so both
 * fields ride the query). `idempotencyKey` is required, as on every resource-creating POST, and
 * scopes dedup to one upload action: a retry returns
 * the existing job, a deliberate re-upload (fresh key) re-extracts. This one schema is the HTTP
 * contract and the agent's `extractSupplierInvoice` tool input. It is strict so a near-miss key
 * from the model (`projectId`, `matterId`) is refused rather than dropped, which would file the
 * draft as overhead behind a 200.
 */
export const CreateSupplierInvoiceExtractionQuerySchema = z.strictObject({
    idempotencyKey: z.string().uuid(),
    clientProjectId: LowercaseUuidSchema.optional().describe('The client project to attribute the resulting draft expense to. Pass it whenever the user names a client project; omit it for overhead. It decides the draft\'s default ledger account, which a later edit does not re-pick.'),
});
export const SupplierInvoiceExtractionStatusSchema = z.enum([
    'PENDING',
    'PROCESSING',
    'COMPLETED',
    'FAILED',
]);
/**
 * Document-level classification from the model. Drives the UI outcome:
 * INVOICE proceeds normally; BANK_STATEMENT and OTHER hard-reject (no pre-fill);
 * UNCERTAIN soft-warns. The pipeline classifies a bank statement but never
 * extracts its fields. `NOT_INVOICE` is a legacy value: rows classified before
 * the widening still carry it and must read back through response validation, but
 * the model no longer emits it (the model-facing schema excludes it). `INVOICE`
 * stays first: an e2e mock returns the first enum member, so do not reorder.
 */
export const SupplierInvoiceDocumentTypeSchema = z.enum([
    'INVOICE',
    'BANK_STATEMENT',
    'OTHER',
    'UNCERTAIN',
    'NOT_INVOICE', // legacy: rows classified before the widening; the model no longer emits it
]);
/**
 * Agent/user verified ground-truth document label. Unlike
 * SupplierInvoiceDocumentTypeSchema, this excludes the legacy NOT_INVOICE value:
 * new verification statements must use OTHER for non-invoice documents.
 */
export const VerifiedDocumentTypeSchema = z.enum(['INVOICE', 'BANK_STATEMENT', 'OTHER', 'UNCERTAIN']);
export const RecordExtractionVerificationInputSchema = z.object({
    storedEmailId: z.uuid(),
    attachmentId: z.uuid(),
    extractionId: z.uuid().optional().nullable(),
    extractionDocumentType: SupplierInvoiceDocumentTypeSchema.optional().nullable(),
    verifiedDocumentType: VerifiedDocumentTypeSchema,
});
export const RecordExtractionVerificationResponseSchema = z.object({
    recorded: z.boolean(),
});
/** Per-field review outcome. `missing` = absent on the PDF; `review` = present but flagged. */
export const FieldReviewSchema = z.object({
    status: z.enum(['ok', 'review', 'missing']),
    reason: z.string().optional(),
});
/**
 * Normalised extracted values, in the units the supplier-invoice create endpoint
 * expects (minor units, basis points), so the frontend can pre-fill directly.
 */
export const SupplierInvoiceExtractedDataSchema = z.object({
    supplierName: z.string().nullable(),
    supplierVatNumber: z.string().nullable(),
    // Supplier contact details, read off the invoice to pre-fill a new supplier
    // and to match an existing one. Plain `.nullable()` (no default): the
    // extraction feature is super-admin-only and pre-release, so there are no
    // persisted COMPLETED rows predating these fields to read back.
    supplierEmail: z.string().nullable(),
    supplierPhone: z.string().nullable(),
    // Supplier postal address, read off the issuer's letterhead or footer. Six
    // flat fields matching BusinessEntity's columns, so a matched or newly created
    // supplier can be populated without reshaping.
    //
    // Every field below is a plain `.nullable()`, NOT `.default(null)`: a zod
    // default makes a schema's input type differ from its output type, which
    // breaks the `ZodType<T, T>` invariant `packages/api`'s `api()` helper
    // requires of every response schema. That constraint binds this RESPONSE
    // schema only - the extraction domain's `readExtractedData` re-uses this same
    // schema to PARSE the stored JSON column, offering null for any key a
    // pre-existing row predates, so the wire contract stays strict without the
    // legacy gap leaking through.
    supplierAddressLine1: z.string().nullable(),
    supplierAddressLine2: z.string().nullable(),
    supplierAddressCity: z.string().nullable(),
    supplierAddressProvince: z.string().nullable(),
    supplierAddressPostalCode: z.string().nullable(),
    supplierAddressCountry: z.string().nullable(),
    supplierInvoiceNumber: z.string().nullable(),
    // "Our reference" (the supplier's own) and "Your reference" (ours) as printed.
    supplierOwnReference: z.string().nullable(),
    customerReference: z.string().nullable(),
    // "Account" / "Account No." - our customer code in the supplier's system.
    // Persisted onto the Supplier, not the invoice, since it identifies the
    // trading relationship rather than the document.
    customerAccountNumber: z.string().nullable(),
    // ── Supplier banking details: where this supplier asks to be paid ──────────
    //
    // Read from the "Banking details" / "Payment details" block. Captured per
    // document on purpose: a supplier's banking details changing between invoices
    // is the classic invoice-redirection fraud signal, so the as-printed values
    // are kept even once the supplier has a stored bank account to compare them
    // against. Where an invoice prints more than one banking block, these hold the
    // primary (first) one. Stamped onto the created expense's own frozen snapshot
    // columns (see `SupplierInvoice.bankName` et al.) by the extraction domain's
    // `supplier-invoice.created` listener - never accepted on the wire.
    bankName: z.string().nullable(),
    bankAccountHolderName: z.string().nullable(),
    bankAccountNumber: z.string().nullable(),
    bankBranchCode: z.string().nullable(),
    /**
     * Account type as printed ("Cheque", "Current", "Savings", "Transmission").
     * Free text rather than an enum: it is transcribed from a document, some banks
     * print their own wording, and an enum would have to reject the unexpected
     * value rather than record it.
     */
    bankAccountType: z.string().nullable(),
    // ── Recipient particulars: OUR details as the supplier printed them ────────
    //
    // Read from the "Bill To" / "Invoice To" / "Customer" block, never from the
    // issuer's letterhead. These are compared against our own BusinessEntity to
    // check the s20(4)(c) particulars, and are NOT written back onto any record -
    // they describe what the supplier believes about us, which is exactly the
    // thing being audited. The address is split into the same six parts as the
    // supplier address so each part carries its own review verdict and a
    // postal-code-only agreement can be distinguished from a whole-address one.
    recipientName: z.string().nullable(),
    recipientVatNumber: z.string().nullable(),
    /**
     * Company registration number as printed. Not a s20(4) particular, so it
     * never drives a compliance verdict on its own - captured because a
     * contradiction here is strong evidence of a document made out to a
     * different entity.
     */
    recipientRegistrationNumber: z.string().nullable(),
    recipientAddressLine1: z.string().nullable(),
    recipientAddressLine2: z.string().nullable(),
    recipientAddressCity: z.string().nullable(),
    recipientAddressProvince: z.string().nullable(),
    recipientAddressPostalCode: z.string().nullable(),
    recipientAddressCountry: z.string().nullable(),
    date: z.string().nullable(),
    dueDate: z.string().nullable(),
    currency: z.string(),
    subtotalMinorUnits: z.number().int().nullable(),
    vatAmountMinorUnits: z.number().int().nullable(),
    totalMinorUnits: z.number().int().nullable(),
    vatRateBasisPoints: z.number().int().nullable(),
    /**
     * The VAT a non-ZAR document prints in Rand (cents); null when none is printed
     * and always null on a ZAR document. Optional so a bundle built against this
     * schema still parses a response from a backend revision that predates the field.
     * TODO: make required once every deployed revision writes it.
     */
    statedZarVatAmountMinorUnits: z.number().int().nullable().optional(),
});
// =============================================================================
// Shadow line items (extraction-only; no supplier-invoice domain model yet)
// =============================================================================
//
// Phase-1 shadow capture: the vision model transcribes the document's printed
// line items alongside the header fields, and a deterministic review reconciles
// their sum against the header amounts. Stored on the extraction row and exposed
// through the admin monitor plus an account-owned, MCP-disabled subresource. The
// main extraction response and supplier-invoice create flow do not read them.
/**
 * What a transcribed line is. `ITEM` is a printed purchase line (a discount is a
 * negative ITEM); `TIP` is a gratuity added to the printed total - usually
 * handwritten - which is not part of the VAT-bearing supply, so the amount paid
 * can exceed the printed total by the tip.
 */
export const SupplierInvoiceExtractedLineItemKindSchema = z.enum(['ITEM', 'TIP']);
/**
 * Per-line VAT marker as printed. SA till slips flag zero-rated items with a
 * symbol (`*`, `#`, "Zero-rated"); tax invoices sometimes label lines exempt.
 * `UNMARKED` = the line carries no VAT marker (the common case).
 */
export const SupplierInvoiceExtractedLineItemVatIndicatorSchema = z.enum([
    'STANDARD',
    'ZERO_RATED',
    'EXEMPT',
    'UNMARKED',
]);
/**
 * One transcribed line, normalised to minor units. Amount fields are null when
 * the model could not read them (or the read value was implausible); a line with
 * a null `lineTotalMinorUnits` makes the sum reconciliation UNSUMMABLE rather
 * than failing the extraction.
 */
export const SupplierInvoiceExtractedLineItemSchema = z.object({
    kind: SupplierInvoiceExtractedLineItemKindSchema,
    description: z.string().nullable(),
    quantity: z.number().nullable(),
    unitAmountMinorUnits: z.number().int().nullable(),
    lineTotalMinorUnits: z.number().int().nullable(),
    vatIndicator: SupplierInvoiceExtractedLineItemVatIndicatorSchema,
    /** True when the line is handwritten (a tip, a pen correction) rather than printed. */
    handwritten: z.boolean(),
});
/**
 * Which header amount the ITEM-line sum reconciled to (1-cent tolerance):
 * - `SUBTOTAL`: lines are VAT-exclusive (tax-invoice style).
 * - `TOTAL`: lines are VAT-inclusive (till-slip style).
 * - `TOTAL_WITH_TIP`: item lines + tip lines equal the total paid (the printed
 *   items are VAT-inclusive and a gratuity bridges printed total -> amount paid).
 * - `NONE`: summable but matched no header amount (see discrepancyMinorUnits).
 * - `UNSUMMABLE`: at least one ITEM line total was unreadable.
 * - `NO_LINES`: the model returned no lines for an invoice-classified document.
 */
export const SupplierInvoiceLineReconciliationSchema = z.enum([
    'SUBTOTAL',
    'TOTAL',
    'TOTAL_WITH_TIP',
    'NONE',
    'UNSUMMABLE',
    'NO_LINES',
]);
/**
 * How a document's line amounts PRINTED: INCLUSIVE (till-slip style, VAT
 * inside each amount) or EXCLUSIVE (tax-invoice style, net of VAT).
 *
 * Extraction-only: it answers which column the extractor read, nothing more.
 * The supplier-invoice domain's stored lines carry their own net/VAT split
 * directly and have no basis column - see `supplierInvoiceLineAmountsReconcile`
 * in `supplier-invoice.ts`.
 */
export const SupplierInvoiceLineItemsBasisSchema = z.enum(['INCLUSIVE', 'EXCLUSIVE']);
/**
 * The line-amount basis implied by a reconciliation verdict, or null when the
 * verdict earns no basis at all: SUBTOTAL means ex-VAT tax-invoice lines
 * (EXCLUSIVE); TOTAL and TOTAL_WITH_TIP mean VAT-inclusive till-slip lines
 * (INCLUSIVE); NONE / UNSUMMABLE / NO_LINES reconciled to nothing, so there is
 * no basis on which their sums could be re-verified.
 *
 * Shared for the same reason as `LINE_SUM_TOLERANCE_MINOR`: the backend's
 * graduation mapper and the create form's pre-submit prediction must agree by
 * construction. If they disagreed, the form would offer lines the server then
 * silently declined to store (or predict a drop that never happens) - and both
 * symptoms look exactly like the routine "user edited the pre-filled amounts"
 * case, so neither would be noticed.
 */
export function reconciliationToBasis(reconciliation) {
    switch (reconciliation) {
        case 'SUBTOTAL':
            return 'EXCLUSIVE';
        case 'TOTAL':
        case 'TOTAL_WITH_TIP':
            return 'INCLUSIVE';
        case 'NONE':
        case 'UNSUMMABLE':
        case 'NO_LINES':
            return null;
    }
}
/**
 * Deterministic shadow verdict over the transcribed lines - the measurement
 * substrate for whether line extraction is trustworthy enough to become a real
 * domain model. Derived by the extraction domain, never by the model.
 */
export const SupplierInvoiceLineItemsReviewSchema = z.object({
    lineCount: z.number().int(),
    /** True when the model stopped transcribing before the end of a long document. */
    truncated: z.boolean(),
    hasTipLine: z.boolean(),
    hasHandwrittenLine: z.boolean(),
    /** The model's own declaration of the line-amount basis; null = it could not tell. */
    declaredAmountsIncludeVat: z.boolean().nullable(),
    reconciliation: SupplierInvoiceLineReconciliationSchema,
    /**
     * Whether the model's declared basis agrees with the basis the sum actually
     * reconciled to. Null when there was no declaration or no reconciliation to
     * check against.
     */
    declarationConsistent: z.boolean().nullable(),
    /** Sum of ITEM line totals in minor units; null when any ITEM line was unreadable. */
    itemSumMinorUnits: z.number().int().nullable(),
    /**
     * Absolute distance (minor units) between the best-matching header candidate
     * and the reconciled sum - 0 for an exact match, the smallest miss for NONE.
     * Null when unsummable or no header amount was available to compare against.
     */
    discrepancyMinorUnits: z.number().int().nullable(),
});
/**
 * Account-owned shadow-line read. Kept separate from SupplierInvoiceExtractionSchema
 * because that schema is also the response contract of active chat/MCP tools.
 */
export const SupplierInvoiceExtractionLineItemsResponseSchema = z.object({
    extractedLineItems: z.array(SupplierInvoiceExtractedLineItemSchema).nullable(),
    lineItemsReview: SupplierInvoiceLineItemsReviewSchema.nullable(),
});
/** One review verdict per extracted field. Keys mirror SupplierInvoiceExtractedData. */
export const SupplierInvoiceFieldReviewSchema = z.object({
    supplierName: FieldReviewSchema,
    supplierVatNumber: FieldReviewSchema,
    supplierEmail: FieldReviewSchema,
    supplierPhone: FieldReviewSchema,
    // Address parts and the new references review as one verdict each, keyed
    // exactly like SupplierInvoiceExtractedData. Strict for the same reason as
    // the data schema above: a key a pre-existing row predates is filled with a
    // `missing` verdict when `readFieldReview` parses the column, not by a
    // schema default.
    supplierAddressLine1: FieldReviewSchema,
    supplierAddressLine2: FieldReviewSchema,
    supplierAddressCity: FieldReviewSchema,
    supplierAddressProvince: FieldReviewSchema,
    supplierAddressPostalCode: FieldReviewSchema,
    supplierAddressCountry: FieldReviewSchema,
    supplierInvoiceNumber: FieldReviewSchema,
    supplierOwnReference: FieldReviewSchema,
    customerReference: FieldReviewSchema,
    customerAccountNumber: FieldReviewSchema,
    bankName: FieldReviewSchema,
    bankAccountHolderName: FieldReviewSchema,
    bankAccountNumber: FieldReviewSchema,
    bankBranchCode: FieldReviewSchema,
    bankAccountType: FieldReviewSchema,
    // Recipient particulars review as one verdict each, on the same presence
    // basis as everything above. They additionally carry a MATCH/MISMATCH verdict
    // against our own record, which is a different axis and lives in
    // `recipientValidation` rather than here - this field's `review` status is
    // presence and legibility only.
    recipientName: FieldReviewSchema,
    recipientVatNumber: FieldReviewSchema,
    recipientRegistrationNumber: FieldReviewSchema,
    recipientAddressLine1: FieldReviewSchema,
    recipientAddressLine2: FieldReviewSchema,
    recipientAddressCity: FieldReviewSchema,
    recipientAddressProvince: FieldReviewSchema,
    recipientAddressPostalCode: FieldReviewSchema,
    recipientAddressCountry: FieldReviewSchema,
    date: FieldReviewSchema,
    dueDate: FieldReviewSchema,
    currency: FieldReviewSchema,
    subtotalMinorUnits: FieldReviewSchema,
    vatAmountMinorUnits: FieldReviewSchema,
    totalMinorUnits: FieldReviewSchema,
    vatRateBasisPoints: FieldReviewSchema,
    // Optional for the same deploy-window reason as the data field it reviews.
    statedZarVatAmountMinorUnits: FieldReviewSchema.optional(),
});
/**
 * Extraction job, returned by both the create (POST) and poll (GET) endpoints.
 * `extractedData` / `fieldReview` are null until status is COMPLETED;
 * `failureReason` is set only when status is FAILED.
 *
 * `conversationId` is set for chat-initiated extractions; null for manual
 * web-form uploads. `draftSupplierInvoiceId` is set once the completion
 * listener has called createDraftFromExtraction (chat rows only).
 */
export const SupplierInvoiceExtractionSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    status: SupplierInvoiceExtractionStatusSchema,
    documentType: SupplierInvoiceDocumentTypeSchema.nullable(),
    extractedData: SupplierInvoiceExtractedDataSchema.nullable(),
    fieldReview: SupplierInvoiceFieldReviewSchema.nullable(),
    /**
     * Deterministic check of the recipient particulars this document printed
     * against our own business entity (VAT Act s20(4)(c)). Null until COMPLETED,
     * on a non-invoice classification, and on every row extracted before the
     * check shipped - readers must treat null as "not checked", never as "clean".
     */
    recipientValidation: RecipientParticularsValidationSchema.nullable(),
    matchedSupplierId: z.string().nullable(),
    /**
     * Set when this invoice has already been recorded as an expense (same supplier
     * + invoice number). The create form warns and deep-links to it. Null otherwise.
     */
    duplicateSupplierInvoiceId: z.string().nullable(),
    failureReason: z.string().nullable(),
    /**
     * Set for chat-initiated extractions; null for manual web-form uploads.
     * The completion listener uses this to decide whether to call
     * createDraftFromExtraction after processing.
     */
    conversationId: z.string().nullable(),
    /**
     * Set by the completion listener after it calls createDraftFromExtraction.
     * Points to the DRAFT SupplierInvoice created from this extraction. Null for
     * manual-upload rows and chat rows whose listener has not yet run.
     */
    draftSupplierInvoiceId: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// =============================================================================
// Admin extraction monitor (super-admin read-only viewer)
// =============================================================================
//
// Cross-account list + detail of extraction jobs, used to manually monitor
// extraction quality against the source document. Distinct from the
// account-scoped poll endpoints above: these carry no MCP surface and are
// gated by requireSuperAdmin.
/**
 * Query-string schema for the admin extraction list endpoint.
 * - `from`/`to`: ISO datetime range (required; applied to `createdAt`).
 * - `accountId`: restrict to a single account.
 * - `status`: filter by job status.
 * - `cursor`: opaque keyset cursor returned by the previous page (`nextCursor`).
 * - `limit`: page size; defaults to 50, capped at 100.
 */
export const AdminSupplierInvoiceExtractionQuerySchema = z.object({
    from: z.iso.datetime({ error: 'from must be a valid ISO datetime string' }),
    to: z.iso.datetime({ error: 'to must be a valid ISO datetime string' }),
    accountId: z.uuid().optional(),
    status: SupplierInvoiceExtractionStatusSchema.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(50),
});
/**
 * One row in the admin extraction list. Carries the at-a-glance quality signals
 * (status, document type, supplier-match / duplicate / draft links) but omits
 * the heavy `extractedData` / `fieldReview` JSON, which the detail endpoint
 * returns.
 */
export const AdminSupplierInvoiceExtractionSummarySchema = z.object({
    id: z.string(),
    accountId: z.string(),
    status: SupplierInvoiceExtractionStatusSchema,
    documentType: SupplierInvoiceDocumentTypeSchema.nullable(),
    matchedSupplierId: z.string().nullable(),
    duplicateSupplierInvoiceId: z.string().nullable(),
    draftSupplierInvoiceId: z.string().nullable(),
    conversationId: z.string().nullable(),
    failureReason: z.string().nullable(),
    /**
     * Roll-up only, so the monitor list can be scanned for compliance outliers
     * without pulling each row's full verdict. The detail response carries the
     * per-particular breakdown in `recipientValidation`.
     */
    recipientValidationStatus: RecipientParticularsStatusSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminSupplierInvoiceExtractionListResponseSchema = createPaginatedResponseSchema(AdminSupplierInvoiceExtractionSummarySchema);
/** Path params for the admin extraction detail and document endpoints. */
export const AdminSupplierInvoiceExtractionParamsSchema = z.object({
    extractionId: z.uuid(),
});
/**
 * Source-document metadata for the detail view. `null` when the extraction's
 * document row is missing (e.g. an orphaned chat-path upload whose extraction
 * row was never persisted). Bytes are served separately by the binary download
 * endpoint, not embedded here.
 */
export const AdminSupplierInvoiceExtractionDocumentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
});
/**
 * Full detail for one extraction: every summary field plus the normalised
 * `extractedData`, the per-field `fieldReview`, and the source-document
 * metadata. `extractedData` / `fieldReview` are null until the job COMPLETED.
 */
export const AdminSupplierInvoiceExtractionDetailSchema = AdminSupplierInvoiceExtractionSummarySchema.extend({
    documentId: z.string(),
    createdByUserId: z.string().nullable(),
    extractedData: SupplierInvoiceExtractedDataSchema.nullable(),
    fieldReview: SupplierInvoiceFieldReviewSchema.nullable(),
    recipientValidation: RecipientParticularsValidationSchema.nullable(),
    /**
     * Shadow-extracted line items + their deterministic reconciliation verdict.
     * The account-owned MCP-disabled line-items subresource also exposes these
     * fields. Null on rows extracted before shadow line capture shipped, on
     * non-COMPLETED rows, and on non-invoice classifications.
     */
    extractedLineItems: z.array(SupplierInvoiceExtractedLineItemSchema).nullable(),
    lineItemsReview: SupplierInvoiceLineItemsReviewSchema.nullable(),
    /**
     * What those extracted lines actually became on the expense, for side-by-side
     * comparison with the capture above - the surface for answering "did these
     * graduate, on which basis, and which captured fields did not come across".
     *
     * Null when no expense was created from this extraction at all. An expense
     * that exists but took no lines reports `lines: []`, which is a different
     * observation (edited amounts, or a verdict that did not reconcile) and is
     * deliberately distinguishable from the null.
     */
    graduated: z
        .object({
        supplierInvoiceId: z.string(),
        lines: z.array(SupplierInvoiceLineItemSchema),
    })
        .nullable(),
    document: AdminSupplierInvoiceExtractionDocumentSchema.nullable(),
});
// =============================================================================
// Transcribed line amounts -> the stored net/VAT split
// =============================================================================
/**
 * The basis on which a capture's lines are read, for EVERY capture that has
 * lines at all.
 *
 * `reconciliationToBasis` above answers a narrower question - which basis the
 * line sum CORROBORATED - and returns null for NONE, UNSUMMABLE and NO_LINES.
 * Both sides of the wire used that null as a storage gate, and it was the wrong
 * gate: a document is not unreadable just because its transcription did not add
 * up. A till slip that wraps each item's description onto a second physical
 * line is the case that showed it - the model emits each wrap as its own
 * amount-less line, the verdict lands on UNSUMMABLE, and a capture whose five
 * real lines were all read correctly was discarded whole. There is no longer a
 * header-only expense to fall back to (the header IS the sum of the lines), so
 * discarding is strictly worse than seeding an imperfect set the user can edit:
 * the difference against the document's own printed total is flagged on the
 * form and refused at approval either way, but only one of the two leaves
 * anything on screen to correct.
 *
 * Preference order, strongest evidence first:
 *  1. The basis the sum corroborated. Arithmetic agreement with a header amount
 *     is the only self-checking signal available, so it always wins.
 *  2. The model's own `declaredAmountsIncludeVat` - it read the document and
 *     said which column those amounts are. Until now this fed only
 *     `declarationConsistent`; when the sum corroborates nothing it is the best
 *     remaining evidence.
 *  3. Whichever basis lands the derived total nearer the document's own printed
 *     total. Deterministic, and derived from figures already captured.
 *
 * Ties, and the no-evidence case (an unreadable printed total AND no
 * declaration), resolve to INCLUSIVE: a till slip is the shape that dominates
 * real uploads, and it is the shape whose lines are hardest to re-read by hand.
 *
 * Returns null only when there is nothing to place on a basis at all - no lines.
 *
 * SHARED for the same reason `reconciliationToBasis` is: the backend's
 * graduation mapper and the Bill create form's seed must agree by construction,
 * or the same document stores a different breakdown depending on whether the
 * expense came from the form or from a chat-initiated draft.
 */
export function resolveExtractedLineItemsBasis(lines, review, header) {
    const corroborated = reconciliationToBasis(review.reconciliation);
    if (corroborated !== null)
        return corroborated;
    if (lines.length === 0)
        return null;
    if (review.declaredAmountsIncludeVat !== null) {
        return review.declaredAmountsIncludeVat ? 'INCLUSIVE' : 'EXCLUSIVE';
    }
    const printedTotal = header.totalMinorUnits;
    if (printedTotal === null)
        return 'INCLUSIVE';
    // Resolved through the same function that will actually split these lines,
    // rather than a shortcut sum, so the basis is chosen on the totals the
    // caller will really store.
    const derivedTotal = (basis) => resolveExtractedLineNetAndVat(lines, basis, header, review.reconciliation).reduce((sum, line) => sum + line.netAmountMinorUnits + line.vatAmountMinorUnits, 0);
    const inclusiveMiss = Math.abs(derivedTotal('INCLUSIVE') - printedTotal);
    const exclusiveMiss = Math.abs(derivedTotal('EXCLUSIVE') - printedTotal);
    return exclusiveMiss < inclusiveMiss ? 'EXCLUSIVE' : 'INCLUSIVE';
}
/**
 * Resolve every transcribed line's net/VAT split from its printed amount and
 * its VAT marker, then place the rounding residual so the set sums to the
 * header EXACTLY.
 *
 * SHARED for the same reason `reconciliationToBasis` above is: two callers on
 * opposite sides of the wire must agree, byte for byte, about what a document's
 * lines become. The Bill create form derives the `lineItems` it submits with
 * this, and extraction graduation derives what a CHAT-initiated draft stores
 * with it. Until now each held its own hand-written copy, and the frontend's
 * even carried a comment naming this promotion as the natural follow-up - the
 * duplication was known, not accidental. A drift between them would not throw:
 * it would silently store a different breakdown depending on which path created
 * the expense, and since both sets still reconcile with the same header, no
 * check downstream would notice.
 *
 * Returns net/VAT pairs ALIGNED BY INDEX with `lines`, and nothing else, so
 * each caller maps its own surrounding shape (the write payload's
 * `ledgerAccountId: null`, the graduated row's display fields) rather than
 * having one caller's output shape imposed on the other.
 *
 * Per-line rules:
 *  - A TIP is never VAT-bearing on either basis: its whole printed amount is
 *    net, VAT zero.
 *  - ZERO_RATED / EXEMPT likewise take zero VAT.
 *  - STANDARD takes the SA standard rate; UNMARKED takes the header's effective
 *    rate over the TAXABLE base only, which on a genuinely mixed-rate document
 *    is a blended snapshot.
 *  - EXCLUSIVE basis treats the printed amount as net and adds VAT on top;
 *    INCLUSIVE de-grosses it via `extractInclusiveVat`.
 *
 * The taxable base excludes every zero-VAT line the header's declared net
 * ACTUALLY CONTAINS. Dividing the header's VAT by a net that still includes an
 * untaxed line dilutes the rate by exactly that line, and the diluted rate is
 * then charged to the lines that are taxable - so the untaxed line is deducted
 * twice. A till slip of R280.00 of food plus an untaxed R30.00 gratuity folded
 * into the header declares net 273.48 + VAT 36.52; 36.52/273.48 is 13.35%, and
 * de-grossing the food at 13.35% yields VAT of 32.98 - R3.54 short of the 36.52
 * the slip prints, on a document nothing misread. Subtracting the gratuity
 * first gives 36.52/243.48 = 15.00%, the rate actually charged. An untaxed
 * line's net equals its printed amount on BOTH bases (that is what the zero-VAT
 * branch above computes), so the subtraction is the same for each.
 *
 * Which lines the header contains is NOT the same question as which lines are
 * untaxed, and getting it wrong overshoots in the other direction - see the
 * `tipIsInsideHeader` comment in the body.
 *
 * Residual: applying a rate per line rounds per line, so the sums can miss the
 * header by a minor unit or two. The difference (net and VAT independently)
 * lands on the ITEM line with the largest net magnitude. Only ITEM lines are
 * eligible - a TIP's split is a printed fact, not a rate-derived value this
 * step is correcting - and when there is no ITEM line at all the residual is
 * left unplaced.
 *
 * A ROUNDING residual is absorbed; a DISCREPANCY is not. `Math.round` can be
 * out by at most one minor unit per line, so a residual within
 * `lines.length` either way is arithmetic this step created and should clean
 * up. Anything larger means the transcription and the document's own declared
 * totals genuinely disagree - a misread line - and sweeping it would silently
 * rewrite the largest line to make the disagreement vanish (a document whose
 * lines add up to R10,000 more than its printed subtotal would have R10,000
 * subtracted from its biggest line, which then looks perfectly reconciled).
 * The lines are returned exactly as transcribed in that case, so the
 * disagreement survives to where it can be surfaced: the derived total then
 * differs from this capture's own `totalMinorUnits`, which the create form
 * flags while the user still has the document open.
 *
 * Callers pass only lines whose `lineTotalMinorUnits` is non-null (an
 * unreadable amount cannot be stored); an unreadable one is treated as 0 here
 * rather than thrown on, because rejecting an unreadable capture is the
 * caller's own gate.
 */
export function resolveExtractedLineNetAndVat(lines, basis, header, reconciliation) {
    const isUntaxed = (line) => line.kind === 'TIP' || line.vatIndicator === 'ZERO_RATED' || line.vatIndicator === 'EXEMPT';
    // Which untaxed lines the header's declared net actually CONTAINS - only
    // those may be subtracted from it, and the two kinds answer differently:
    //
    //  - A zero-rated or exempt ITEM is always inside. Every verdict that
    //    corroborates a basis does so by matching the ITEM sum against a header
    //    amount, so an ITEM is part of the declared figures by construction.
    //  - A TIP is inside only under TOTAL_WITH_TIP, the one verdict that says
    //    items PLUS tips make the declared total. It is also the exact condition
    //    the normaliser's own tip recovery corroborates before folding a
    //    gratuity into the subtotal, so the two agree by construction.
    //
    // Subtracting a tip the header never contained is not a smaller error than
    // failing to subtract one it did: it inflates the rate charged to every
    // taxable line. A slip whose header covers the printed R280.00 only, with a
    // R30.00 gratuity transcribed beyond it, declares net 243.48 + VAT 36.52;
    // subtracting the gratuity anyway gives 36.52/213.48 = 17.11% and de-grosses
    // the food to 239.09 + 40.91, overstating input VAT by R4.39 on a document
    // nothing misread. Under any verdict that corroborated nothing there is no
    // evidence either way, and the tip stays in the base - understating a claim
    // is the safer of the two errors.
    const tipIsInsideHeader = reconciliation === 'TOTAL_WITH_TIP';
    const untaxedNetInsideHeader = lines.reduce((sum, line) => {
        if (!isUntaxed(line))
            return sum;
        if (line.kind === 'TIP' && !tipIsInsideHeader)
            return sum;
        return sum + (line.lineTotalMinorUnits ?? 0);
    }, 0);
    // The header's net less the untaxed lines it contains: the base its declared
    // VAT was actually charged on. A base of zero or less (all lines untaxed, or
    // a transcription whose untaxed lines exceed the declared net) yields rate 0
    // from the helper, which leaves the disagreement to surface rather than
    // inventing a rate to hide it.
    const headerVatRateBasisPoints = effectiveVatRateBasisPoints(header.subtotalMinorUnits - untaxedNetInsideHeader, header.vatAmountMinorUnits);
    const resolved = lines.map((line) => {
        const printed = line.lineTotalMinorUnits ?? 0;
        if (isUntaxed(line)) {
            return { netAmountMinorUnits: printed, vatAmountMinorUnits: 0 };
        }
        const rateBasisPoints = line.vatIndicator === 'STANDARD' ? SA_VAT_RATE_BASIS_POINTS : headerVatRateBasisPoints;
        if (basis === 'EXCLUSIVE') {
            return {
                netAmountMinorUnits: printed,
                vatAmountMinorUnits: Math.round((printed * rateBasisPoints) / 10000),
            };
        }
        const { net, vat } = extractInclusiveVat(printed, rateBasisPoints);
        return { netAmountMinorUnits: net, vatAmountMinorUnits: vat };
    });
    // The residual has to compare LIKE WITH LIKE: only the lines the header
    // describes, on the same `tipIsInsideHeader` rule the rate base uses. A
    // header covering the printed items alone, measured against a line set that
    // also carries a gratuity, misses by the whole gratuity - which reads as a
    // discrepancy far past tolerance, so a genuine one-cent rounding drift on the
    // items never gets cleaned up and the derived subtotal lands a cent off the
    // figure the document states.
    const insideHeader = (index) => lines[index].kind !== 'TIP' || tipIsInsideHeader;
    const sumInsideHeader = (pick) => resolved.reduce((sum, l, index) => (insideHeader(index) ? sum + pick(l) : sum), 0);
    const netResidual = header.subtotalMinorUnits - sumInsideHeader((l) => l.netAmountMinorUnits);
    const vatResidual = header.vatAmountMinorUnits - sumInsideHeader((l) => l.vatAmountMinorUnits);
    if (netResidual === 0 && vatResidual === 0)
        return resolved;
    // One minor unit per line is the most `Math.round` above can have introduced;
    // beyond that the lines and the declared header genuinely disagree and the
    // transcription stands as read. Counted over the lines the header describes,
    // for the same reason the sums above are. See the "rounding residual vs
    // discrepancy" paragraph in the docblock.
    const roundingTolerance = lines.filter((_line, index) => insideHeader(index)).length;
    if (Math.abs(netResidual) > roundingTolerance || Math.abs(vatResidual) > roundingTolerance) {
        return resolved;
    }
    let largestIndex = -1;
    for (let index = 0; index < resolved.length; index += 1) {
        if (lines[index].kind !== 'ITEM')
            continue;
        if (largestIndex === -1 ||
            Math.abs(resolved[index].netAmountMinorUnits) >
                Math.abs(resolved[largestIndex].netAmountMinorUnits)) {
            largestIndex = index;
        }
    }
    if (largestIndex !== -1) {
        resolved[largestIndex].netAmountMinorUnits += netResidual;
        resolved[largestIndex].vatAmountMinorUnits += vatResidual;
    }
    return resolved;
}
